import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useMemo, useCallback } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { defaultPUR5020_R01DetailDto, PUR5020_R01DetailDto } from 'src/containers/mes/PUR/PUR5020_R01/Dto_PUR5020_R01';
import { usePUR5020_R01Store } from 'src/containers/mes/PUR/PUR5020_R01/Store_PUR5020_R01';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { getDefaultRowIndexColumnConfig, defaultColDef } from 'src/utils/grid/gridStyleUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'PUR5020_R01-detail-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<PUR5020_R01DetailDto> | null>;
}

export const DetailTable_PUR5020_R01 = (props: Props) => {
	const { gridRef } = props;
	const {
		PUR5020_R01DetailList,
		selectedPUR5020_R01DetailList,
		setSelectedPUR5020_R01DetailList,
		deletedPUR5020_R01DetailList,
		initSelectedPUR5020_R01DetailList,
		detailColumnState,
		setDetailColumnState,
		detailScrollTop,
		setDetailScrollTop,
	} = usePUR5020_R01Store();
	const { inoutClassList } = useCommonCodeStore();

	const headerNames: Record<string, string> = {
		rowIndex: '',
		txDt: '입고일자',
		warehouseNm: '수불창고명',
		partnerNm: '거래처명',
		inoutClass: '수불유형구분',
		inQty: '입고량',
		outQty: '출고량',
		balanceQty: '재고량',
	};

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: PUR5020_R01DetailList,
		defaultGridData: defaultPUR5020_R01DetailDto,
		selectedRow: selectedPUR5020_R01DetailList
			? PUR5020_R01DetailList.find(item => String(item.valueIndex) === selectedPUR5020_R01DetailList)
			: undefined,
	});

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'valueIndex')(params), []);

	const baseColumnDefs: ColDef<PUR5020_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '입고일자', colId: 'txDt', field: 'txDt', width: 120,
			editable: false,
			valueFormatter: (p) => {
				if (p.data?.txDtDisplay) return p.data.txDtDisplay;
				if (p.value) {
					const date = new Date(p.value);
					return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
				}
				return '';
			},
		},
		{
			headerName: '수불창고명', colId: 'warehouseNm', field: 'warehouseNm', width: 100,
			editable: false,
		},
		{
			headerName: '수불유형구분', colId: 'inoutClass', field: 'inoutClass', width: 120,
			editable: false,
			valueFormatter: (p) => {
				if (!p.value) return '';
				const codeItem = inoutClassList.find(item => item.minorCd === p.value);
				return codeItem?.codeNm || p.value;
			},
		},
		{
			headerName: '거래처명', colId: 'partnerNm', field: 'partnerNm', width: 120,
			editable: false,
		},
		{
			headerName: '입고량', colId: 'inQty', field: 'inQty', width: 100,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'inQty'),
			valueFormatter: (p) => p.value ? p.value.toLocaleString() : '0',
		},
		{
			headerName: '출고량', colId: 'outQty', field: 'outQty', width: 100,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'outQty'),
			valueFormatter: (p) => p.value ? p.value.toLocaleString() : '0',
		},
		{
			headerName: '재고량', colId: 'balanceQty', field: 'balanceQty', width: 100,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'balanceQty'),
			valueFormatter: (p) => p.value ? p.value.toLocaleString() : '0',
		},
	], [getCellStyle, inoutClassList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<PUR5020_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<PUR5020_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [detailColumnState, settingRowIndex, baseColumnDefs, headerNames]);

	const pinnedBottomRow = useMemo(() => {
		const totals = PUR5020_R01DetailList.reduce((acc, item) => ({
			inQty: acc.inQty + (Number(item.inQty) || 0),
			outQty: acc.outQty + (Number(item.outQty) || 0),
		}), { inQty: 0, outQty: 0 });
		const balanceQty = totals.inQty - totals.outQty;
		return [{
			valueIndex: '',
			rowIndex: '',
			txDt: '',
			inoutClass: '',
			inQty: totals.inQty,
			outQty: totals.outQty,
			balanceQty,
			editable: false,
		}];
	}, [PUR5020_R01DetailList]);

	const normalizedSelectedIds = useMemo(() => {
		if (!selectedPUR5020_R01DetailList) return [];
		if (/^\d+$/.test(String(selectedPUR5020_R01DetailList))) return [Number(selectedPUR5020_R01DetailList)];
		return [];
	}, [selectedPUR5020_R01DetailList]);

	const rowClassRules = useMemo(() => createRowClassRules<PUR5020_R01DetailDto>({
		idKey: 'valueIndex',
		selectedList: normalizedSelectedIds,
		deletedList: deletedPUR5020_R01DetailList,
	}), [createRowClassRules, normalizedSelectedIds, deletedPUR5020_R01DetailList]);

	const handleRowClick = useCallback((e: { node?: { rowPinned?: string | null }; data?: PUR5020_R01DetailDto }) => {
		if (e.node?.rowPinned === 'bottom') return;
		const row = e.data;
		if (!row || typeof row.valueIndex !== 'number') return;
		const id = String(row.valueIndex);
		if (selectedPUR5020_R01DetailList === id) {
			setSelectedPUR5020_R01DetailList(undefined);
		} else {
			setSelectedPUR5020_R01DetailList(id);
		}
	}, [selectedPUR5020_R01DetailList, setSelectedPUR5020_R01DetailList]);

	return (
		<GridContextWrapper<PUR5020_R01DetailDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={PUR5020_R01DetailList}
			initSelected={initSelectedPUR5020_R01DetailList}
			defaultRowData={defaultPUR5020_R01DetailDto}
			allSelected={() => {}}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<PUR5020_R01DetailDto>
				ref={gridRef}
				rowData={PUR5020_R01DetailList}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
				getDefaultColumnState={() => defaultColumnState}
				rowClassRules={rowClassRules}
				onRowClicked={handleRowClick}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}
			/>
		</GridContextWrapper>
	);
};

export default DetailTable_PUR5020_R01;
